import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveDocumentsBoxBig, Trash16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenDeleteGoalsDialog } from 'shared/contexts/delete-goals';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { Goal } from 'shared/types/goal';

import { MenuGroups } from './types';

export type LifeCycleListProps = {
  goals: Goal[];
  onClose: () => void;
  registerItem?: (
    group: MenuGroups,
    index: number,
    element: HTMLButtonElement | null,
  ) => void;
  registerGroup?: MenuGroups;
};

export const LifeCycleList: React.FC<LifeCycleListProps> = ({
  goals,
  onClose,
  registerItem,
  registerGroup,
}) => {
  const { t } = useTranslation();
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const openDeleteGoalsDialog = useOpenDeleteGoalsDialog();

  const allArchived = useMemo(
    () => goals.every((g) => !!g.archivedAt),
    [goals],
  );

  const onArchive = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const newArchivedAt = allArchived ? null : new Date(); // If all archived, unarchive. Else archive.

      goals.forEach((goal) => {
        // Only update if needed
        if (!!goal.archivedAt !== !!newArchivedAt) {
          updateGoal({ goal: { id: goal.id, archivedAt: newArchivedAt } });
        }
      });
      onClose();
    },
    [allArchived, goals, onClose, updateGoal],
  );

  const onDelete = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openDeleteGoalsDialog(goals.map((g) => g.id));
      onClose();
    },
    [goals, onClose, openDeleteGoalsDialog],
  );

  return (
    <PopupMenuList hasBottomBorder>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onArchive}
          start={<Icon icon={ArchiveDocumentsBoxBig} />}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 0, el)}
        >
          {t(
            allArchived
              ? 'context-menus.goal.labels.de-archive'
              : 'context-menus.goal.labels.archive',
          )}
        </PopupMenuButton>
      </PopupMenuListItem>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onDelete}
          start={<Icon icon={Trash16} />}
          startColor={Colors.Error}
          textColor={Colors.Error}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 1, el)}
        >
          {t('context-menus.goal.labels.delete')}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
